/*eslint  @typescript-eslint/no-use-before-define: 0*/

import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store/index";
import {
  checkIsPaymentPrintedOnDocument,
  getCalculatePaymentSummary,
  getDataForEditPayment,
  getDataForNewPayment,
  getRecalculateValueInDocumentCurrencyAfterPaymentAmountInCurrencyChange,
  postAddNewPayment,
  postEditPayment,
  removePayment,
} from "@/requests/payments";
import { PAYMENT_VM_STATE } from "@/static/paymentVmState";
import { EditPayment, DataAfterAddPayment } from "@/models/api/payments/EditPayment";
import { ConfirmModel } from "@/models/components/popup/Confirm";
import { filterCapitalize } from "@/filters/capitalize";
import i18n from "@/plugins/i18n";
import { CreateDocumentCashDocumentModel, CashDocumentItemEdit } from "@/models/api/finances/CashDocumentEdit";
import dayjs from "dayjs";
import { DEFAULT_DATE_FORMAT } from "@/static/dateFormats";
import { DOCUMENT_TYPES } from "@/static/documentTypes";
import { getDocumentContractor } from "@/requests/document";
import { KpContractor } from "@/models/api/Contractor";
import { DoubleConfirm } from "@/models/api/DoubleConfirm";

export interface PaymentsStateInterface {
  isDialog: boolean;
  paymentId: number | null;
  editPayment: EditPayment | null;
  kpKwModel: CreateDocumentCashDocumentModel | null;
}

export const PaymentsState: PaymentsStateInterface = {
  isDialog: false,
  paymentId: null,
  editPayment: null,
  kpKwModel: null,
};

const PaymentsModule = defineModule({
  state: PaymentsState as PaymentsStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setIsDialog(state, param: boolean) {
      state.isDialog = param;
    },
    setPaymentId(state, param: number | null) {
      state.paymentId = param;
    },
    setEditPayment(state, param: EditPayment | null) {
      state.editPayment = param;
    },
    setKpKwModel(state, param: CreateDocumentCashDocumentModel | null) {
      state.kpKwModel = param;
    },
  },
  actions: {
    async editPayment(context, param: { paymentId: number; orderId: string }): Promise<void> {
      const { commit } = _moduleActionContext(context);
      commit.setPaymentId(param.paymentId);
      commit.setEditPayment(null);
      commit.setIsDialog(true);
      const data = await getDataForEditPayment(param.paymentId, param.orderId);
      commit.setEditPayment(data);
    },
    async newPayment(context, param: { currentId: string; state: PAYMENT_VM_STATE }): Promise<void> {
      const { commit } = _moduleActionContext(context);
      commit.setPaymentId(null);
      commit.setEditPayment(null);
      const data = await getDataForNewPayment(param.currentId, param.state);
      commit.setIsDialog(true);
      commit.setEditPayment(data);
    },
    async savePayment(context): Promise<void> {
      const { rootDispatch, state, commit } = _moduleActionContext(context);
      let data!: DataAfterAddPayment;
      if (state.paymentId === null) {
        data = await postAddNewPayment(state.editPayment as EditPayment);
      } else {
        const response = await checkIsPaymentPrintedOnDocument(state.paymentId);
        if (response) {
          const isConfirmed = (await rootDispatch.popup.addConfirm(
            new ConfirmModel(
              filterCapitalize(i18n.t("paymentIsPrintedOnDocumentAreYouSureToEdit")),
              filterCapitalize(i18n.t("warning")),
              [
                {
                  text: filterCapitalize(i18n.t("no")),
                  color: "warning",
                  value: false,
                  outlined: true,
                },
                {
                  text: filterCapitalize(i18n.t("yes")),
                  color: "warning",
                  value: true,
                },
              ],
              "550px"
            )
          )) as boolean;
          if (isConfirmed) await postEditPayment(state.editPayment as EditPayment, state.paymentId);
          return;
        } else {
          data = await postEditPayment(state.editPayment as EditPayment, state.paymentId);
        }
      }
      rootDispatch.popup.addSavedOkSnackbar();
      commit.setIsDialog(false);
      if (data.canPrintCashDocument) {
        const isConfirmed = (await rootDispatch.popup.addConfirm(
          new ConfirmModel(
            filterCapitalize(i18n.t("doYouWantToAddKPKWToPayment")),
            filterCapitalize(i18n.t("warning")),
            [
              {
                text: filterCapitalize(i18n.t("no")),
                color: "warning",
                value: false,
                outlined: true,
              },
              {
                text: filterCapitalize(i18n.t("yes")),
                color: "warning",
                value: true,
              },
            ],
            "550px"
          )
        )) as boolean;
        if (isConfirmed) {
          commit.setKpKwModel(null);
          const model = new CreateDocumentCashDocumentModel();
          const cashbox = state.editPayment?.cashboxList.find((x) => x.id == data.cashboxId ?? null);
          const document = state.editPayment?.documentsList.find((x) => x.id == data.documentId);
          if (cashbox && state.editPayment?.paymentAmountInCurrency && document) {
            model.paymentId = data.paymentId ?? null;
            model.cashboxId = cashbox?.id;
            model.issueDate = dayjs(state.editPayment?.addDate).format(DEFAULT_DATE_FORMAT);
            model.description = "";
            model.amount = null;
            model.documentType =
              state.editPayment?.paymentAmountInCurrency > 0
                ? DOCUMENT_TYPES.CashIn
                : state.editPayment?.paymentAmountInCurrency < 0
                ? DOCUMENT_TYPES.CashOut
                : null;

            const documentContractor = await getDocumentContractor(document.id);

            model.contractor = {
              city: documentContractor.city,
              country: documentContractor.country,
              importedContractorId: documentContractor.id,
              isIndividual: documentContractor.isIndividual,
              name: documentContractor.name,
              surname: documentContractor.surname,
              nationalBusinessRegistryNumber: documentContractor.nationalBusinessRegistryNumber,
              postalCode: documentContractor.postalCode,
              shortName: documentContractor.shortName,
              streetAndNumber: documentContractor.streetAndNumber,
              tin: documentContractor.tin,
            } as KpContractor;

            const cashDocumentItemEdit = new CashDocumentItemEdit();
            cashDocumentItemEdit.documentId = null;
            cashDocumentItemEdit.name = document.documentNumber;
            cashDocumentItemEdit.amountInCurrency = state.editPayment?.paymentAmountInCurrency;
            model.documentItems = [cashDocumentItemEdit];
            const exchangeRate = await rootDispatch.exchangeRate.getLastExchangeRate({
              exchangeDate: model.issueDate,
              currency: cashbox?.currency ?? "PLN",
              skipAlert: true,
            });
            model.exchangeDate = exchangeRate.exchangeDate;
            model.exchangeRate = exchangeRate.exchangeRateValue;
            model.isDialog = true;
            commit.setKpKwModel(model);
          }
        }
      }
    },

    async recalculatePaymentSummary(context): Promise<void> {
      const { state, commit } = _moduleActionContext(context);
      if (state.editPayment !== null) {
        const editPayment = state.editPayment;
        const summary = await getCalculatePaymentSummary({
          paymentAmount: editPayment.paymentAmount,
          editedPaymentId: state.paymentId,
          paymentAmountInDocumentCurrency: editPayment.paymentReferenceAmount,
          documentId: editPayment.selectedDocumentId,
        });
        editPayment.grossInCurrencyLeftToPay = summary.grossInCurrencyLeftToPay;
        editPayment.grossLeftToPay = summary.grossLeftToPay;
        commit.setEditPayment(editPayment);
      }
    },
    async recalculateAfterPaymentAmountInCurrencyChange(context): Promise<void> {
      const { state, commit, dispatch } = _moduleActionContext(context);
      if (state.editPayment !== null) {
        let editPayment = state.editPayment;
        editPayment.paymentReferenceAmount =
          await getRecalculateValueInDocumentCurrencyAfterPaymentAmountInCurrencyChange({
            paymentCurrency: editPayment.paymentCurrency,
            documentId: editPayment.selectedDocumentId,
            value: editPayment.paymentAmountInCurrency,
            exchangeDate: editPayment.exchangeDate,
          });
        editPayment = await dispatch.recalculatePaymentAmount(editPayment);
        commit.setEditPayment(editPayment);
      }
    },
    async recalculateAfterPaymentReferenceAmountChange(context): Promise<void> {
      const { state, commit, dispatch } = _moduleActionContext(context);
      if (state.editPayment !== null) {
        const editPayment = await dispatch.recalculatePaymentAmount(state.editPayment);
        commit.setEditPayment(editPayment);
      }
    },
    async recalculateAfterExchangeRateChange(context): Promise<void> {
      const { state, commit, rootDispatch } = _moduleActionContext(context);
      if (state.editPayment !== null) {
        const editPayment = state.editPayment;
        if (editPayment.paymentCurrency !== editPayment.documentCurrency) {
          editPayment.paymentAmountInCurrency = await rootDispatch.exchangeRate.getCalculatedValueInCurrencyFromValue({
            value: editPayment.paymentAmount,
            exchangeRate: editPayment.exchangeRate,
          });
        } else {
          editPayment.paymentAmount = await rootDispatch.exchangeRate.getCalculatedValueFromValueInCurrency({
            valueInCurrency: editPayment.paymentAmountInCurrency,
            exchangeRate: editPayment.exchangeRate,
          });
        }
        commit.setEditPayment(editPayment);
      }
    },
    async recalculatePaymentAmount(context, editPayment: EditPayment) {
      const { rootState, rootDispatch } = _moduleActionContext(context);
      if (
        rootState.user.officeSettings !== null &&
        editPayment.documentCurrency !== rootState.user.officeSettings.baseCurrency
      ) {
        editPayment.paymentAmount = await rootDispatch.exchangeRate.getCalculatedValueFromValueInCurrency({
          valueInCurrency: editPayment.paymentReferenceAmount,
          exchangeRate:
            editPayment.paymentCurrency !== editPayment.documentCurrency
              ? editPayment.referenceExchangeRate
              : editPayment.exchangeRate,
        });
      } else {
        editPayment.paymentAmount = editPayment.paymentReferenceAmount;
      }
      return editPayment;
    },
    async removePayment(context, paymentId: number): Promise<void> {
      const { rootDispatch } = _moduleActionContext(context);
      rootDispatch.failIfRequestsInProgress();
      if (
        await rootDispatch.popup.addConfirm(
          new ConfirmModel(
            filterCapitalize(i18n.t("inCaseOfCashPaymentCashDocumentWillBeModifiedAreYouSureToRemove?")),
            filterCapitalize(i18n.t("warning")),
            [
              {
                text: filterCapitalize(i18n.t("no")),
                color: "warning",
                value: false,
                outlined: true,
              },
              {
                text: filterCapitalize(i18n.t("yes")),
                color: "warning",
                value: true,
              },
            ],
            "450px"
          )
        )
      ) {
        const result = await removePayment(paymentId, false);
        await DoubleConfirm.Process(
          result,
          rootDispatch.popup.addConfirm,
          async () => {
            await removePayment(paymentId, true);
          },
          async () => {
            rootDispatch.popup.addOperationSuccessfulSnackbar();
          }
        );
      }
    },
  },
});

export default PaymentsModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, PaymentsModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, PaymentsModule);
